import { toDate, toMilliseconds } from '@art-suite/art-core-ts';

import type { ApiScanNote, ScanNoteType } from '@kaiizen/kai-pro-api';

/** The scope-approval note types an activity log shows, and how each reads there. */
const SCOPE_APPROVAL_NOTE_NAME: Partial<Record<ScanNoteType, string>> = {
  editRequest: 'Edit Requested',
  editResolution: 'Edit Request Resolved',
  editApproval: 'Edit Request Approved',
  approval: 'Approved',
};

/** One scope-approval note on a task, in a task's activity log. */
export type TaskScopeNoteRow = {
  id: string;
  at: Date;
  actorUserId: string;
  noteType: ScanNoteType;
  name: string;
  /** The note's text as it reads now: its latest edit's, if it was edited. */
  body?: string;
};

const byCreatedAt = (a: ApiScanNote, b: ApiScanNote) => toMilliseconds(a.createdAt) - toMilliseconds(b.createdAt);

/**
 * A task's scope-approval notes (edit requests, their resolutions and approvals, and approvals) as
 * activity-log rows, newest first: each at the time it was written, by its author. Works from the raw
 * notes, not the flattened ones, since flattening folds a reply into the note it answers. Deleted
 * notes are left out.
 *
 * @param scanNotes - the scan's raw notes, every type, for any scan part
 */
export const getTaskScopeNoteRows = ({ scanNotes, taskScanPartId }: {
  scanNotes: readonly ApiScanNote[];
  taskScanPartId: string;
}): TaskScopeNoteRow[] => {
  const deletedNoteIds = new Set(scanNotes.filter(note => note.scanNoteType === 'delete').map(note => note.parentScanNoteId));
  const latestBodyByNoteId = new Map(
    [...scanNotes].filter(note => note.scanNoteType === 'edit').sort(byCreatedAt).map(edit => [edit.parentScanNoteId, edit.body]),
  );
  return scanNotes
    .filter(note => note.scanPartId === taskScanPartId && SCOPE_APPROVAL_NOTE_NAME[note.scanNoteType] && !deletedNoteIds.has(note.id))
    .sort(byCreatedAt)
    .reverse()
    .map(note => ({
      id: `note-${note.id}`,
      at: toDate(note.createdAt),
      actorUserId: note.userId,
      noteType: note.scanNoteType,
      name: SCOPE_APPROVAL_NOTE_NAME[note.scanNoteType]!,
      body: (latestBodyByNoteId.get(note.id) ?? note.body).trim() || undefined,
    }));
};
